import React, { useState, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Alert, RefreshControl } from 'react-native';
import { Text, Card, Chip, ActivityIndicator, Divider } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import api from '../../services/api';

const PERIODOS = [
  { key: 'hoy', label: 'Hoy' },
  { key: 'esta_semana', label: 'Semana' },
  { key: 'este_mes', label: 'Mes' },
  { key: 'este_ano', label: 'Año' },
];

const fmtMoney = (n) => 'L. ' + (Number(n) || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => (parseInt(n) || 0).toLocaleString('es-HN');

const ConsolidadoScreen = () => {
  const [periodo, setPeriodo] = useState('este_mes');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const cargar = useCallback(async (p) => {
    try {
      const res = await api.getConsolidado(p);
      setData(res);
    } catch (e) {
      Alert.alert('Error', e.message || 'No se pudo cargar el consolidado');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { setLoading(true); cargar(periodo); }, [cargar, periodo]));

  const cambiarPeriodo = (p) => { setPeriodo(p); setLoading(true); cargar(p); };

  const totales = data?.totales || {};
  const sucursales = data?.sucursales || [];
  const maxTotal = Math.max(1, ...sucursales.map((s) => s.total_ventas || 0));

  return (
    <View style={styles.container}>
      <View style={styles.periodoBar}>
        {PERIODOS.map((p) => (
          <Chip
            key={p.key}
            selected={periodo === p.key}
            onPress={() => cambiarPeriodo(p.key)}
            style={[styles.periodoChip, periodo === p.key && styles.periodoChipSel]}
            textStyle={periodo === p.key ? styles.periodoChipTextSel : styles.periodoChipText}
          >
            {p.label}
          </Chip>
        ))}
      </View>

      {loading ? (
        <View style={styles.center}><ActivityIndicator size="large" color="#007bff" /></View>
      ) : (
        <ScrollView
          contentContainerStyle={styles.scroll}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); cargar(periodo); }} />}
        >
          {/* Totales del grupo */}
          <Card style={styles.totalCard}>
            <Card.Content>
              <Text style={styles.totalTitle}>Total del grupo</Text>
              <Text style={styles.totalVentas}>{fmtMoney(totales.total_ventas)}</Text>
              <View style={styles.totalRow}>
                <View style={styles.totalItem}>
                  <Text style={styles.totalItemNum}>{fmtInt(totales.numero_ventas)}</Text>
                  <Text style={styles.totalItemLbl}>Ventas</Text>
                </View>
                <View style={styles.totalItem}>
                  <Text style={styles.totalItemNum}>{fmtInt(totales.productos_vendidos)}</Text>
                  <Text style={styles.totalItemLbl}>Productos</Text>
                </View>
                <View style={styles.totalItem}>
                  <Text style={[styles.totalItemNum, { color: (totales.ganancia || 0) >= 0 ? '#2e7d32' : '#c62828' }]}>
                    {fmtMoney(totales.ganancia)}
                  </Text>
                  <Text style={styles.totalItemLbl}>Ganancia</Text>
                </View>
              </View>
            </Card.Content>
          </Card>

          <Text style={styles.subtitle}>Por sucursal</Text>

          {sucursales.map((s) => (
            <Card key={s.id} style={styles.sucCard}>
              <Card.Content>
                <View style={styles.sucHeader}>
                  <Ionicons name="business" size={16} color="#007bff" />
                  <Text style={styles.sucNombre}>{s.nombre}</Text>
                  {s.es_matriz ? <Text style={styles.matrizTag}>Matriz</Text> : null}
                </View>
                {/* barra proporcional al total de ventas */}
                <View style={styles.barTrack}>
                  <View style={[styles.barFill, { width: `${Math.round(((s.total_ventas || 0) / maxTotal) * 100)}%` }]} />
                </View>
                <Text style={styles.sucTotal}>{fmtMoney(s.total_ventas)}</Text>
                <Divider style={styles.div} />
                <View style={styles.sucMetrics}>
                  <Text style={styles.metric}>{fmtInt(s.numero_ventas)} ventas</Text>
                  <Text style={styles.metric}>{fmtInt(s.productos_vendidos)} prod.</Text>
                  <Text style={[styles.metric, { color: (s.ganancia || 0) >= 0 ? '#2e7d32' : '#c62828' }]}>
                    {fmtMoney(s.ganancia)}
                  </Text>
                </View>
              </Card.Content>
            </Card>
          ))}

          {sucursales.length === 0 && (
            <Text style={styles.empty}>Sin datos para este período.</Text>
          )}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 60 },
  periodoBar: { flexDirection: 'row', padding: 10, backgroundColor: '#fff', justifyContent: 'space-around', elevation: 2 },
  periodoChip: { backgroundColor: '#eef2f7' },
  periodoChipSel: { backgroundColor: '#007bff' },
  periodoChipText: { color: '#555' },
  periodoChipTextSel: { color: '#fff', fontWeight: 'bold' },
  scroll: { padding: 16, paddingBottom: 40 },
  totalCard: { backgroundColor: '#0d47a1', marginBottom: 16 },
  totalTitle: { color: 'rgba(255,255,255,0.8)', fontSize: 13 },
  totalVentas: { color: '#fff', fontSize: 30, fontWeight: 'bold', marginVertical: 6 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 8 },
  totalItem: { alignItems: 'center', flex: 1 },
  totalItemNum: { color: '#fff', fontSize: 15, fontWeight: 'bold' },
  totalItemLbl: { color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 2 },
  subtitle: { fontSize: 14, fontWeight: 'bold', color: '#444', marginBottom: 8 },
  sucCard: { backgroundColor: '#fff', marginBottom: 10 },
  sucHeader: { flexDirection: 'row', alignItems: 'center' },
  sucNombre: { fontSize: 15, fontWeight: 'bold', color: '#222', marginLeft: 6, flex: 1 },
  matrizTag: { fontSize: 10, color: '#007bff', backgroundColor: '#e3f2fd', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6, overflow: 'hidden' },
  barTrack: { height: 8, backgroundColor: '#eef2f7', borderRadius: 4, marginTop: 10, overflow: 'hidden' },
  barFill: { height: 8, backgroundColor: '#42a5f5', borderRadius: 4 },
  sucTotal: { fontSize: 18, fontWeight: 'bold', color: '#0d47a1', marginTop: 6 },
  div: { marginVertical: 8 },
  sucMetrics: { flexDirection: 'row', justifyContent: 'space-between' },
  metric: { fontSize: 12, color: '#666', fontWeight: '600' },
  empty: { textAlign: 'center', color: '#888', marginTop: 30 },
});

export default ConsolidadoScreen;
